import { Control, Controller, RegisterOptions } from "react-hook-form";
import { NoxusFormLabel } from "./NoxusFormLabel";
import { Combobox, ComboboxOption } from "@/components/shadcn/combobox";
import { cn } from "@/lib/utils";
import { useState } from "react";
import { ConfirmationDialog } from "@/components/Dialogs/ConfirmationDialog";

export type OptionType = ComboboxOption;
interface ISpotSelect {
  name?: string;
  value?: string | { label: string; value: string };
  placeholder?: string;
  options: OptionType[];
  label?: string;
  tooltip?: string;
  defaultValue?: OptionType;
  helperText?: string;
  errorMessage?: string;
  isLoading?: boolean;
  isDisabled?: boolean;
  isClearable?: boolean;
  isNotSearchable?: boolean;
  isHorizontal?: boolean;
  onChange?: (value: any) => void;
  onBlur?: (value: any) => void;
  isOptional?: boolean;
  showOptionalLabel?: boolean;
  hasPortal?: boolean;
  onSearchChange?: (search: string) => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  customItemContent?: (
    option?: ComboboxOption,
    selected?: boolean
  ) => React.ReactNode;
}
interface ISpotSelectFieldProps<ClearValue extends string | null = string> {
  name: string;
  placeholder?: string;
  label?: string;
  helperText?: string;
  errorMessage?: string;
  tooltip?: string;
  options: OptionType[];
  rules?: RegisterOptions;
  control: Control<any>;
  isClearable?: boolean;
  clearValue?: ClearValue;
  isLoading?: boolean;
  isDisabled?: boolean;
  isNotSearchable?: boolean;
  isHorizontal?: boolean;
  onChange?: (value: string | NoInfer<ClearValue>) => void;
  defaultValue?: string;
  isOptional?: boolean;
  showOptionalLabel?: boolean;
  needsConfirmation?: boolean;
  confirmationMessage?: string;
  hasPortal?: boolean;
  onSearchChange?: (search: string) => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  customItemContent?: (
    option?: ComboboxOption,
    selected?: boolean
  ) => React.ReactNode;
}

export const SpotSelect = ({
  value,
  options,
  placeholder,
  label,
  tooltip,
  helperText,
  errorMessage,
  onChange,
  isLoading = false,
  isDisabled = false,
  isClearable = false,
  isNotSearchable = false,
  isHorizontal = false,
  defaultValue,
  name,
  isOptional,
  showOptionalLabel,
  hasPortal,
  onSearchChange,
  onLoadMore,
  hasMore,
  isLoadingMore,
  customItemContent,
}: ISpotSelect) => {
  const currentValue =
    typeof value === "string" || typeof value === "number"
      ? value
      : value?.value;

  const handleChange = (selectedValue: string | undefined) => {
    if (onChange) {
      if (!selectedValue) {
        onChange(null);
      } else {
        const selectedOption = options.find((o) => o.value === selectedValue);
        onChange(selectedOption);
      }
    }
  };

  return isHorizontal ? (
    <div
      className={cn(
        "flex justify-between gap-6 relative",
        helperText ? "items-start" : "items-center"
      )}
    >
      {label && (
        <div className="flex-1">
          <NoxusFormLabel
            label={label}
            tooltip={tooltip}
            name={name}
            showOptionalLabel={showOptionalLabel}
            isRequired={!isOptional}
            noMargin={true}
          />
          {helperText != undefined && (
            <p className="text-muted-foreground text-xs whitespace-pre-line mt-1.5">
              {helperText}
            </p>
          )}
        </div>
      )}

      <div className="flex-1">
        <Combobox
          // NoxusFormLabel renders htmlFor={name}; without a matching id the
          // label pointed at nothing, so clicking it focused nothing.
          id={name}
          options={options}
          value={currentValue}
          placeholder={placeholder}
          onValueChange={handleChange}
          disabled={isDisabled}
          loading={isLoading}
          clearable={isClearable}
          searchable={!isNotSearchable}
          hasPortal={hasPortal}
          onSearchChange={onSearchChange}
          onLoadMore={onLoadMore}
          hasMore={hasMore}
          isLoadingMore={isLoadingMore}
          customItemContent={customItemContent}
        />
      </div>
    </div>
  ) : (
    <div className="w-full">
      {label && (
        <NoxusFormLabel
          label={label}
          tooltip={tooltip}
          name={name}
          showOptionalLabel={showOptionalLabel}
          isRequired={!isOptional}
        />
      )}

      <Combobox
        id={name}
        options={options}
        value={currentValue}
        placeholder={placeholder}
        onValueChange={handleChange}
        disabled={isDisabled}
        loading={isLoading}
        clearable={isClearable}
        searchable={!isNotSearchable}
        hasPortal={hasPortal}
        onSearchChange={onSearchChange}
        onLoadMore={onLoadMore}
        hasMore={hasMore}
        isLoadingMore={isLoadingMore}
        customItemContent={customItemContent}
      />

      {helperText != undefined && (
        <p
          className="text-muted-foreground text-xs whitespace-pre-line"
          style={{ whiteSpace: "pre-line", marginTop: "0.25rem" }}
        >
          {helperText}
        </p>
      )}
      {errorMessage && (
        <p className="text-destructive text-xs mt-1">{errorMessage}</p>
      )}
    </div>
  );
};

export const SpotSelectField = <ClearValue extends string | null = string>(
  props: ISpotSelectFieldProps<ClearValue>
) => {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingValue, setPendingValue] = useState<any>(null);
  const [pendingOnChange, setPendingOnChange] = useState<(() => void) | null>(
    null
  );

  const defaultValue = props.options.find(
    (option) => option.value === props.defaultValue
  );

  const handleConfirm = () => {
    if (pendingOnChange) {
      pendingOnChange();
    }
    setShowConfirmation(false);
    setPendingValue(null);
    setPendingOnChange(null);
  };

  const handleCancel = () => {
    setShowConfirmation(false);
    setPendingValue(null);
    setPendingOnChange(null);
  };

  return (
    <>
      <Controller
        control={props.control}
        name={props.name}
        defaultValue={props.defaultValue}
        rules={props.rules}
        render={({ field: { onChange, value, onBlur } }) => {
          const handleChange = (selectedOption: any) => {
            if (!selectedOption) {
              const cleared =
                props.clearValue === undefined ? "" : props.clearValue;
              props.onChange?.(cleared);
              onChange(cleared);
              return;
            }

            const optionValue =
              typeof selectedOption === "string"
                ? selectedOption
                : selectedOption.value;

            if (props.needsConfirmation) {
              setPendingValue(optionValue);
              setPendingOnChange(() => () => {
                onChange(optionValue);
                if (props.onChange) {
                  props.onChange(optionValue);
                }
              });
              setShowConfirmation(true);
            } else {
              onChange(optionValue);
              if (props.onChange) {
                props.onChange(optionValue);
              }
            }
          };

          return (
            <SpotSelect
              value={value}
              label={props.label}
              options={props.options}
              tooltip={props.tooltip}
              helperText={props.helperText}
              placeholder={props.placeholder}
              isDisabled={props.isDisabled}
              defaultValue={defaultValue}
              isLoading={props.isLoading}
              isClearable={props.isClearable}
              isNotSearchable={props.isNotSearchable}
              onChange={handleChange}
              onBlur={onBlur}
              name={props.name}
              isHorizontal={props.isHorizontal}
              isOptional={props.isOptional}
              showOptionalLabel={props.showOptionalLabel}
              hasPortal={props.hasPortal}
              onSearchChange={props.onSearchChange}
              onLoadMore={props.onLoadMore}
              hasMore={props.hasMore}
              isLoadingMore={props.isLoadingMore}
              customItemContent={props.customItemContent}
              errorMessage={props.errorMessage}
            />
          );
        }}
      />
      {/* let's do it like this before making it generic all around */}
      {props.needsConfirmation && (
        <ConfirmationDialog
          size="md"
          cancelVariant="outline"
          isOpen={showConfirmation}
          onOpenChange={setShowConfirmation}
          title="Confirm change"
          description={
            props.confirmationMessage ||
            "Are you sure you want to make this change?"
          }
          onConfirm={handleConfirm}
          onCancel={handleCancel}
          confirmText="Continue"
        />
      )}
    </>
  );
};
